import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Button,
  Checkbox,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  createPatientInsurance,
  getAgencyInsurances,
  getPatientInsurance,
  updatePatientInsurance,
} from '../../services/api';
import type {
  InsurancePosition,
  PatientInsuranceCreate,
  PatientInsuranceResponse,
  PatientResponse,
  RelationToInsured,
} from '../../services/api/types.gen';
import type { AgencyInsuranceResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { formatSSN } from '../../utils/intake';
import { GENDER_OPTIONS, US_STATES } from '../../constants/options';

// ─── Constants ───────────────────────────────────────────────────────────────

const POSITION_OPTIONS: { value: InsurancePosition; label: string }[] = [
  { value: 'Primary', label: 'Primary' },
  { value: 'Secondary', label: 'Secondary' },
  { value: 'Tertiary', label: 'Tertiary' },
  { value: 'Quaternary', label: 'Quaternary' },
];

const RELATION_OPTIONS: { value: RelationToInsured; label: string }[] = [
  { value: 'Self', label: 'Self' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Child', label: 'Child' },
  { value: 'Other', label: 'Other' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface PatientInsuranceFormDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  clinicId: string | null | undefined;
  patient?: PatientResponse | null;
  editingInsuranceId?: string | null;
}

type FormState = {
  agency_insurance_id: string;
  position: InsurancePosition | '';
  insured_last_name: string;
  insured_first_name: string;
  insured_gender: string;
  insured_ssn: string;
  insured_dob: Date | undefined;
  insured_phone: string;
  insured_employer: string;
  plan_name: string;
  insured_address: string;
  insured_city: string;
  insured_state: string;
  insured_zip: string;
  relation_to_insured: RelationToInsured | '';
  group_name: string;
  group_number: string;
  policy_number: string;
  effective_start: Date | undefined;
  effective_end: Date | undefined;
  assignment_of_benefits: boolean;
  deductible: string;
  coverage_percent: string;
  copay_amount: string;
  insured_signature_on_file: boolean;
};

const EMPTY_FORM: FormState = {
  agency_insurance_id: '',
  position: 'Primary',
  insured_last_name: '',
  insured_first_name: '',
  insured_gender: '',
  insured_ssn: '',
  insured_dob: undefined,
  insured_phone: '',
  insured_employer: '',
  plan_name: '',
  insured_address: '',
  insured_city: '',
  insured_state: '',
  insured_zip: '',
  relation_to_insured: '',
  group_name: '',
  group_number: '',
  policy_number: '',
  effective_start: undefined,
  effective_end: undefined,
  assignment_of_benefits: true,
  deductible: '',
  coverage_percent: '',
  copay_amount: '',
  insured_signature_on_file: true,
};

function patientPrefillForm(patient: PatientResponse): FormState {
  return {
    ...EMPTY_FORM,
    insured_first_name: patient.first_name ?? '',
    insured_last_name: patient.last_name ?? '',
    insured_gender: patient.gender ?? '',
    insured_ssn: patient.ssn ? formatSSN(patient.ssn) : '',
    insured_dob: patient.date_of_birth
      ? (parseISOToLocalDate(patient.date_of_birth) ?? undefined)
      : undefined,
    insured_phone: patient.phone ?? '',
    insured_address: patient.address ?? '',
    insured_city: patient.city ?? '',
    insured_state: patient.state ?? '',
    insured_zip: patient.zip_code ?? '',
    relation_to_insured: 'Self',
  };
}

function insuranceToForm(ins: PatientInsuranceResponse): FormState {
  return {
    agency_insurance_id: ins.agency_insurance_id,
    position: ins.position,
    insured_last_name: ins.insured_last_name ?? '',
    insured_first_name: ins.insured_first_name ?? '',
    insured_gender: ins.insured_gender ?? '',
    insured_ssn: ins.insured_ssn ? formatSSN(ins.insured_ssn) : '',
    insured_dob: ins.insured_dob
      ? (parseISOToLocalDate(ins.insured_dob) ?? undefined)
      : undefined,
    insured_phone: ins.insured_phone ?? '',
    insured_employer: ins.insured_employer ?? '',
    plan_name: ins.plan_name ?? '',
    insured_address: ins.insured_address ?? '',
    insured_city: ins.insured_city ?? '',
    insured_state: ins.insured_state ?? '',
    insured_zip: ins.insured_zip ?? '',
    relation_to_insured: ins.relation_to_insured ?? '',
    group_name: ins.group_name ?? '',
    group_number: ins.group_number ?? '',
    policy_number: ins.policy_number ?? '',
    effective_start: ins.effective_start
      ? (parseISOToLocalDate(ins.effective_start) ?? undefined)
      : undefined,
    effective_end: ins.effective_end
      ? (parseISOToLocalDate(ins.effective_end) ?? undefined)
      : undefined,
    assignment_of_benefits: ins.assignment_of_benefits ?? false,
    deductible: ins.deductible != null ? String(ins.deductible) : '',
    coverage_percent:
      ins.coverage_percent != null ? String(ins.coverage_percent) : '',
    copay_amount: ins.copay_amount != null ? String(ins.copay_amount) : '',
    insured_signature_on_file: ins.insured_signature_on_file ?? false,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseOptionalFloat(val: string): number | null {
  if (!val.trim()) return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
  error?: boolean;
}> = ({ children, required, error }) => (
  <Label
    className={cn(
      'text-xs font-medium',
      error ? 'text-red-500' : 'text-muted-foreground'
    )}
  >
    {children}
    {required && (
      <span
        className={cn('ml-0.5', error ? 'text-red-500' : 'text-destructive')}
      >
        *
      </span>
    )}
  </Label>
);

const StateCombobox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  error?: boolean;
}> = ({ value, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const selected = US_STATES.find(s => s.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          role='combobox'
          aria-expanded={open}
          className={cn(
            'flex h-7 w-full items-center justify-between rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-red-500 focus:ring-red-500',
            !value && 'text-muted-foreground'
          )}
        >
          <span className='truncate'>
            {selected
              ? `${selected.label} (${selected.value})`
              : 'Select state'}
          </span>
          <ChevronsUpDown className='ml-1 h-3.5 w-3.5 shrink-0 opacity-50' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-56 p-0' align='start'>
        <Command>
          <CommandInput placeholder='Search states…' className='h-8 text-xs' />
          <CommandList>
            <CommandEmpty className='py-3 text-center text-xs text-muted-foreground'>
              No state found.
            </CommandEmpty>
            <CommandGroup>
              {US_STATES.map(st => (
                <CommandItem
                  key={st.value}
                  value={st.label}
                  onSelect={() => {
                    onChange(st.value);
                    setOpen(false);
                  }}
                  className='text-xs'
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      value === st.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {st.label} ({st.value})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const InsuranceCombobox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: AgencyInsuranceResponse[];
  error?: boolean;
}> = ({ value, onChange, options, error }) => {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(ai => ai.id === value);

  const handleListWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const listElement = listRef.current;
    if (!listElement || listElement.scrollHeight <= listElement.clientHeight) {
      return;
    }

    listElement.scrollTop += event.deltaY;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          role='combobox'
          aria-expanded={open}
          className={cn(
            'flex h-7 w-full items-center justify-between rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-red-500 focus:ring-red-500',
            !value && 'text-muted-foreground'
          )}
        >
          <span className='truncate'>
            {selected ? selected.name : 'Select insurance'}
          </span>
          <ChevronsUpDown className='ml-1 h-3.5 w-3.5 shrink-0 opacity-50' />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[var(--radix-popover-trigger-width)] p-0'
        align='start'
      >
        <Command>
          <CommandInput
            placeholder='Search insurance…'
            className='h-8 text-xs'
          />
          <CommandList
            ref={listRef}
            className='max-h-60 overscroll-contain'
            onWheelCapture={handleListWheel}
          >
            <CommandEmpty className='py-3 text-center text-xs text-muted-foreground'>
              No insurance found.
            </CommandEmpty>
            <CommandGroup>
              {options.map(ai => (
                <CommandItem
                  key={ai.id}
                  value={ai.name}
                  onSelect={() => {
                    onChange(ai.id);
                    setOpen(false);
                  }}
                  className='text-xs'
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      value === ai.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {ai.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const PatientInsuranceFormDialog: React.FC<
  PatientInsuranceFormDialogProps
> = ({ open, onClose, patientId, clinicId, patient, editingInsuranceId }) => {
  const { error: showError, success: showSuccess } = useToast();

  // ── State ─────────────────────────────────────────────────────────────────
  const [agencyInsurances, setAgencyInsurances] = useState<
    AgencyInsuranceResponse[]
  >([]);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Set<string>>(new Set());

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadAgencyInsurances = useCallback(async () => {
    try {
      const res = await getAgencyInsurances({
        query: clinicId ? { agency_clinic_id: clinicId } : {},
      });
      if (res.data) setAgencyInsurances(res.data);
    } catch (err) {
      console.error('Failed to load agency insurances:', err);
    }
  }, [clinicId]);

  const loadInsuranceForEdit = useCallback(async () => {
    if (!editingInsuranceId) return;
    setIsLoading(true);
    try {
      const res = await getPatientInsurance({
        path: {
          patient_id: patientId,
          patient_insurance_id: editingInsuranceId,
        },
      });
      if (res.data) {
        setForm(insuranceToForm(res.data));
      }
    } catch (err) {
      console.error('Failed to load insurance record:', err);
      showError('Unable to load insurance details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [editingInsuranceId, patientId, showError]);

  useEffect(() => {
    if (open) {
      setErrors(new Set());
      if (editingInsuranceId) {
        void loadInsuranceForEdit();
      } else {
        setForm(patient ? patientPrefillForm(patient) : EMPTY_FORM);
      }
      void loadAgencyInsurances();
    }
  }, [
    open,
    editingInsuranceId,
    patient,
    loadInsuranceForEdit,
    loadAgencyInsurances,
  ]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const newErrors = new Set<string>();
    if (!form.agency_insurance_id) newErrors.add('agency_insurance_id');
    if (!form.position) newErrors.add('position');

    if (
      form.effective_start &&
      form.effective_end &&
      form.effective_start >= form.effective_end
    ) {
      newErrors.add('effective_start');
      newErrors.add('effective_end');
    }

    if (newErrors.size > 0) {
      setErrors(newErrors);
      showError(
        newErrors.has('effective_start')
          ? 'Effective start date must be before end date.'
          : 'Please fill in all required fields.'
      );
      return;
    }

    const payload = {
      agency_insurance_id: form.agency_insurance_id,
      position: form.position as InsurancePosition,
      insured_last_name: form.insured_last_name || null,
      insured_first_name: form.insured_first_name || null,
      insured_gender:
        (form.insured_gender as PatientInsuranceCreate['insured_gender']) ||
        null,
      insured_ssn: form.insured_ssn.replace(/\D/g, '') || null,
      insured_dob: form.insured_dob ? formatDateToISO(form.insured_dob) : null,
      insured_phone: form.insured_phone || null,
      insured_employer: form.insured_employer || null,
      plan_name: form.plan_name || null,
      insured_address: form.insured_address || null,
      insured_city: form.insured_city || null,
      insured_state: form.insured_state || null,
      insured_zip: form.insured_zip || null,
      relation_to_insured:
        (form.relation_to_insured as RelationToInsured) || null,
      group_name: form.group_name || null,
      group_number: form.group_number || null,
      policy_number: form.policy_number || null,
      effective_start: form.effective_start
        ? formatDateToISO(form.effective_start)
        : null,
      effective_end: form.effective_end
        ? formatDateToISO(form.effective_end)
        : null,
      assignment_of_benefits: form.assignment_of_benefits,
      deductible: parseOptionalFloat(form.deductible),
      coverage_percent: parseOptionalFloat(form.coverage_percent),
      copay_amount: parseOptionalFloat(form.copay_amount),
      insured_signature_on_file: form.insured_signature_on_file,
    };

    setIsSaving(true);
    try {
      const response = editingInsuranceId
        ? await updatePatientInsurance({
            path: {
              patient_id: patientId,
              patient_insurance_id: editingInsuranceId,
            },
            body: payload,
          })
        : await createPatientInsurance({
            path: { patient_id: patientId },
            body: payload as PatientInsuranceCreate,
          });

      if (response.error || !response.data) {
        showError(
          getUserFacingApiMessage(
            response,
            'Unable to save insurance. Please try again.'
          )
        );
        return;
      }

      showSuccess(
        editingInsuranceId ? 'Insurance updated.' : 'Insurance added.'
      );
      onClose();
    } catch (err) {
      console.error('Failed to save insurance:', err);
      showError('Unable to save insurance. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>
            {editingInsuranceId ? 'Edit Insurance' : 'Add Insurance'}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            {editingInsuranceId
              ? 'Edit insurance policy'
              : 'Add new insurance policy'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className='py-8 text-center text-sm text-muted-foreground'>
            Loading…
          </div>
        ) : (
          <div className='flex flex-col gap-4 overflow-hidden flex-1 min-h-0'>
            <div className='overflow-y-auto flex-1 min-h-0 pr-1'>
              <div className='space-y-5'>
                {/* Insurance & Position */}
                <div className='grid grid-cols-2 gap-3'>
                  <div className='space-y-1.5'>
                    <FieldLabel
                      required
                      error={errors.has('agency_insurance_id')}
                    >
                      Insurance
                    </FieldLabel>
                    <InsuranceCombobox
                      value={form.agency_insurance_id}
                      onChange={v => setField('agency_insurance_id', v)}
                      options={agencyInsurances}
                      error={errors.has('agency_insurance_id')}
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel required error={errors.has('position')}>
                      Position
                    </FieldLabel>
                    <Select
                      value={form.position}
                      onValueChange={v =>
                        setField('position', v as InsurancePosition)
                      }
                    >
                      <SelectTrigger
                        selectSize='sm'
                        className={
                          errors.has('position')
                            ? 'border-red-500 focus:ring-red-500'
                            : undefined
                        }
                      >
                        <SelectValue placeholder='Select position' />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Insured name / gender / SSN / DOB */}
                <div className='grid grid-cols-2 gap-3'>
                  <div className='space-y-1.5'>
                    <FieldLabel>Insured Last Name</FieldLabel>
                    <Input
                      value={form.insured_last_name}
                      onChange={e =>
                        setField('insured_last_name', e.target.value)
                      }
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Insured First Name</FieldLabel>
                    <Input
                      value={form.insured_first_name}
                      onChange={e =>
                        setField('insured_first_name', e.target.value)
                      }
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Insured Gender</FieldLabel>
                    <Select
                      value={form.insured_gender}
                      onValueChange={v => setField('insured_gender', v)}
                    >
                      <SelectTrigger selectSize='sm'>
                        <SelectValue placeholder='Select gender' />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDER_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Insured SSN</FieldLabel>
                    <Input
                      value={form.insured_ssn}
                      onChange={e =>
                        setField('insured_ssn', formatSSN(e.target.value))
                      }
                      placeholder='XXX-XX-XXXX'
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Insured DOB</FieldLabel>
                    <DatePicker
                      date={form.insured_dob}
                      onDateChange={d => setField('insured_dob', d)}
                      datePickerSize='sm'
                      typable
                      className='w-full'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Insured Phone</FieldLabel>
                    <Input
                      value={form.insured_phone}
                      onChange={e => setField('insured_phone', e.target.value)}
                      inputSize='sm'
                    />
                  </div>
                </div>

                {/* Employer / Plan */}
                <div className='grid grid-cols-2 gap-3'>
                  <div className='space-y-1.5'>
                    <FieldLabel>Insured Employer</FieldLabel>
                    <Input
                      value={form.insured_employer}
                      onChange={e =>
                        setField('insured_employer', e.target.value)
                      }
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Plan Name</FieldLabel>
                    <Input
                      value={form.plan_name}
                      onChange={e => setField('plan_name', e.target.value)}
                      inputSize='sm'
                    />
                  </div>
                </div>

                {/* Insured address */}
                <div className='grid grid-cols-1 gap-3'>
                  <div className='space-y-1.5'>
                    <FieldLabel>Insured Address</FieldLabel>
                    <Input
                      value={form.insured_address}
                      onChange={e =>
                        setField('insured_address', e.target.value)
                      }
                      inputSize='sm'
                    />
                  </div>
                </div>
                <div className='grid grid-cols-3 gap-3'>
                  <div className='space-y-1.5'>
                    <FieldLabel>City</FieldLabel>
                    <Input
                      value={form.insured_city}
                      onChange={e => setField('insured_city', e.target.value)}
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>State</FieldLabel>
                    <StateCombobox
                      value={form.insured_state}
                      onChange={v => setField('insured_state', v)}
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>ZIP</FieldLabel>
                    <Input
                      value={form.insured_zip}
                      onChange={e => setField('insured_zip', e.target.value)}
                      inputSize='sm'
                    />
                  </div>
                </div>

                {/* Relation / Group / Policy */}
                <div className='grid grid-cols-2 gap-3'>
                  <div className='space-y-1.5'>
                    <FieldLabel>Relation to Insured</FieldLabel>
                    <Select
                      value={form.relation_to_insured}
                      onValueChange={v =>
                        setField('relation_to_insured', v as RelationToInsured)
                      }
                    >
                      <SelectTrigger selectSize='sm'>
                        <SelectValue placeholder='Select relation' />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Policy Number</FieldLabel>
                    <Input
                      value={form.policy_number}
                      onChange={e => setField('policy_number', e.target.value)}
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Group Name</FieldLabel>
                    <Input
                      value={form.group_name}
                      onChange={e => setField('group_name', e.target.value)}
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Group Number</FieldLabel>
                    <Input
                      value={form.group_number}
                      onChange={e => setField('group_number', e.target.value)}
                      inputSize='sm'
                    />
                  </div>
                </div>

                {/* Effective dates */}
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-1.5'>
                    <FieldLabel error={errors.has('effective_start')}>
                      Effective Start
                    </FieldLabel>
                    <DatePicker
                      date={form.effective_start}
                      onDateChange={d => setField('effective_start', d)}
                      datePickerSize='sm'
                      typable
                      className='w-full'
                      error={errors.has('effective_start')}
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel error={errors.has('effective_end')}>
                      Effective End
                    </FieldLabel>
                    <DatePicker
                      date={form.effective_end}
                      onDateChange={d => setField('effective_end', d)}
                      datePickerSize='sm'
                      typable
                      className='w-full'
                      error={errors.has('effective_end')}
                    />
                  </div>
                </div>

                {/* Financial */}
                <div className='grid grid-cols-3 gap-3'>
                  <div className='space-y-1.5'>
                    <FieldLabel>Deductible ($)</FieldLabel>
                    <Input
                      value={form.deductible}
                      onChange={e => setField('deductible', e.target.value)}
                      type='number'
                      min='0'
                      step='0.01'
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Coverage (%)</FieldLabel>
                    <Input
                      value={form.coverage_percent}
                      onChange={e =>
                        setField('coverage_percent', e.target.value)
                      }
                      type='number'
                      min='0'
                      max='100'
                      step='0.01'
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <FieldLabel>Co-Pay ($)</FieldLabel>
                    <Input
                      value={form.copay_amount}
                      onChange={e => setField('copay_amount', e.target.value)}
                      type='number'
                      min='0'
                      step='0.01'
                      inputSize='sm'
                    />
                  </div>
                </div>

                {/* Checkboxes */}
                <div className='flex flex-col gap-3'>
                  <div className='flex items-center gap-2'>
                    <Checkbox
                      id='assignment_of_benefits'
                      checked={form.assignment_of_benefits}
                      onCheckedChange={v =>
                        setField('assignment_of_benefits', !!v)
                      }
                      checkboxSize='sm'
                    />
                    <Label
                      htmlFor='assignment_of_benefits'
                      className='text-sm cursor-pointer'
                    >
                      Assignment of Benefits (AOB)
                    </Label>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Checkbox
                      id='insured_sig'
                      checked={form.insured_signature_on_file}
                      onCheckedChange={v =>
                        setField('insured_signature_on_file', !!v)
                      }
                      checkboxSize='sm'
                    />
                    <Label
                      htmlFor='insured_sig'
                      className='text-sm cursor-pointer'
                    >
                      Insured Signature on File
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className='pt-3 border-t flex justify-end gap-2'>
              <Button size='sm' variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button
                size='sm'
                onClick={() => void handleSave()}
                disabled={isSaving}
              >
                {isSaving
                  ? 'Saving…'
                  : editingInsuranceId
                    ? 'Save Changes'
                    : 'Add Insurance'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
